import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Copy, Check, Download, Loader2 } from 'lucide-react';
import { useCallback, useRef } from 'react';
import useTranslation from '@/hooks/useTranslation';
import categoriesColorList from '@/utils/categoriesColorList';
import useCopyState from './useCopyState';
import useDownloadImage from './useDownloadImage';
import {
  BASE_ROW_HEIGHT,
  CHART_MIN_HEIGHT,
  computeYAxisWidth,
  estimateLineCount,
} from './chartHelpers';
import WrappedYAxisTick from './WrappedYAxisTick';

interface ArtifactChartBlockProps {
  title: string;
  data: {
    x_label?: string;
    y_label?: string;
    /**
     * TELV1-377 — `color?` is kept on the type for backward-compat with
     * artifact JSON persisted before the field was retired, but it is
     * NOT read at render time. Bar fills always come from the neutral
     * pastel `categoriesColorList`. The LLM is instructed not to emit
     * the field, and the backend normaliser strips it before persisting
     * new artifacts (see research-assistant.service.ts), so this type
     * exists only to type-check old payloads loaded from MongoDB.
     */
    series: Array<{ name: string; value: number; color?: string }>;
  };
}

const DEFAULT_COLORS = categoriesColorList;

function ArtifactChartBlock({ title, data }: ArtifactChartBlockProps) {
  const { lang } = useTranslation();
  const [copied, triggerCopied] = useCopyState();
  const chartCardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, isDone, triggerDownload] = useDownloadImage(chartCardRef, { title });

  const handleCopy = useCallback(() => {
    const text = data.series.map((s) => `${s.name}\t${s.value}`).join('\n');
    navigator.clipboard
      .writeText(`${title}\n${data.x_label || ''}\t${data.y_label || ''}\n${text}`)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [data, title, triggerCopied]);

  if (!data?.series?.length) return null;

  // TELV1-377 — bar fills come exclusively from the pastel
  // `categoriesColorList` by positional index. Any LLM-emitted
  // `entry.color` is ignored (the LLM was given a `color?` field by
  // earlier prompt iterations and would paint themes arbitrary
  // red/green — see ticket for the F4 finding). The backend
  // normaliser strips colour before persistence, but old artifacts in
  // MongoDB may still carry the field; this renderer drops it silently
  // either way so the chart is always neutral.

  const yAxisWidth = computeYAxisWidth(data.series);

  // Compute chart height: for each row we estimate how many lines the label
  // will wrap to, then use that to scale the row height. A single-line label
  // uses BASE_ROW_HEIGHT; each additional line adds half a row-height.
  const chartHeight = Math.max(
    CHART_MIN_HEIGHT,
    data.series.reduce((total, s) => {
      const lines = estimateLineCount(s.name, yAxisWidth);
      return total + BASE_ROW_HEIGHT + Math.max(0, lines - 1) * (BASE_ROW_HEIGHT * 0.5);
    }, 0),
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <div className="flex items-center gap-0.5">
          {/* Download PNG */}
          <button
            type="button"
            onClick={triggerDownload}
            disabled={isDownloading}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.downloadChartPng')}
            aria-label={lang.get('msg.downloadChartPng')}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {isDownloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isDone ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </button>

          {/* Copy data */}
          <button
            type="button"
            onClick={handleCopy}
            className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            title={lang.get('msg.copyArtifact')}
            aria-label={lang.get('msg.copyArtifact')}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-primary" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      </div>

      {/* White card — this is what gets captured by html-to-image */}
      <div ref={chartCardRef} className="bg-white border border-neutral-200 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={data.series}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: '#737373' }}
              label={
                data.y_label
                  ? { value: data.y_label, position: 'insideBottom', offset: -5, fontSize: 11 }
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              width={yAxisWidth}
              tick={<WrappedYAxisTick width={yAxisWidth} />}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e5e5e5',
                fontSize: 12,
              }}
            />
            {/* isAnimationActive={false} (TELV1-419): the grow-from-zero mount
                animation gets stuck at its 0 start-frame when the chart mounts
                inside the artifact panel (recharts v3 mis-times the first
                layout in the conditionally-rendered / resizable column), so the
                bars render 0px wide until a window resize re-triggers a render.
                The panel doesn't benefit from the animation and disabling it
                also stops the PNG export capturing a mid-tween frame. */}
            <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
              {data.series.map((entry, idx) => (
                <Cell key={entry.name} fill={DEFAULT_COLORS[idx % DEFAULT_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ArtifactChartBlock;
